import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';

// Toast — small, ambient, bottom-center save confirmations.
//
// Pattern mirrors ConfirmDialog: <ToastProvider> mounted once at the app
// root; anywhere in the tree `const toast = useToast()` exposes
// `toast.success(message)` / `toast.error(message)`. Each call queues a
// pill that animates up from the bottom, auto-dismisses after ~3s, and
// stacks below any prior toasts still on screen.
//
// Design choices:
//   - Dark ink background (NOT lime) — lime is reserved for interactive
//     primary actions per the design system. A toast is ambient feedback,
//     not a CTA, so it stays out of the way.
//   - Phosphor icons (filled CheckCircle / WarningCircle), sentence case
//     copy, pill border-radius — matches the rest of the system.
//   - role="status" + aria-live="polite" so screen readers announce the
//     message without interrupting current speech.
//   - Respects prefers-reduced-motion (animation collapses to instant).
//
// Use for: top-of-page save flows where the form stays on screen after
// success. Don't use for dialogs/side panels — those close on success
// and a toast on top of the closing surface is noise.

const DURATION_MS = 3000;

export type ToastTone = 'success' | 'error';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: ReactNode;
}

interface ToastContextValue {
  success: (message: ReactNode) => void;
  error: (message: ReactNode) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  // Track timers so unmounting the provider mid-flight doesn't leak callbacks.
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: ReactNode) => {
      const id = nextId++;
      setItems((prev) => [...prev, { id, tone, message }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DURATION_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const value: ToastContextValue = {
    success: (message) => push('success', message),
    error: (message) => push('error', message),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast: <ToastProvider> is missing from the component tree.',
    );
  }
  return ctx;
}

interface ToastViewportProps {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}

function ToastViewport({ items, onDismiss }: ToastViewportProps) {
  if (items.length === 0) return null;
  return (
    <div
      // pointer-events-none on the container so the toast doesn't block
      // clicks on the page behind it; each pill re-enables pointer events
      // so its dismiss button (if surfaced later) stays clickable.
      className="pointer-events-none fixed inset-x-0 bottom-6 z-[90] flex flex-col items-center gap-2 px-4"
      aria-live="polite"
      role="status"
    >
      {items.map((item) => (
        <ToastPill key={item.id} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastPill({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  // Mount-tick → set 'visible' so the CSS transition runs. Without this
  // the pill renders already-visible and the slide-up animation is
  // skipped on the first toast.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const Icon = item.tone === 'success' ? CheckCircle : WarningCircle;
  const iconClass =
    item.tone === 'success' ? 'text-lime' : 'text-red-400';

  return (
    <div
      className={[
        'pointer-events-auto flex max-w-[min(28rem,calc(100vw-2rem))] items-center gap-2 rounded-pill bg-ink px-4 py-2.5 text-sm font-medium text-white shadow-xl transition-all duration-200',
        'motion-reduce:transition-none',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
      ].join(' ')}
      onClick={() => onDismiss(item.id)}
    >
      <Icon size={18} weight="fill" className={iconClass} aria-hidden />
      <span>{item.message}</span>
    </div>
  );
}
